'use client'

import React from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Avatar } from '@/components/ui/avatar'
import { CoworkerAvatar } from '@/components/shell/CoworkerAvatar'
import { downloadAuthenticatedFile } from '@/lib/api-client'
import { trimUnfinishedBlocks } from '@/lib/ava-stream'
import type { StreamStep } from '@/lib/sse'
import type { ChatMessage as ChatMessageType } from '@/context/AIContext'

/**
 * A markdown link `[label](/api/...)` rendered inline in a chat bubble.
 * This app authenticates API calls via a Bearer token attached per-request
 * (see lib/api-client.ts), not cookies, so a plain `<a href>` to a backend
 * endpoint would 401 on click. Relative /api links (e.g. a generated PDF
 * report) are fetched with auth and saved as a file; anything else opens
 * as a normal external link.
 */
function ChatLink({ label, href }: { label: string; href: string }) {
  const isApiLink = href.startsWith('/api/')
  const [state, setState] = React.useState<'idle' | 'downloading' | 'error'>('idle')

  // An in-app route (for example a driver record, /workbench/drivers/<id>, that
  // Ava links in her answer) opens in the same tab (plan 03 CHG-13).
  if (!isApiLink && href.startsWith('/') && !href.startsWith('//')) {
    return (
      <Link
        href={href}
        className='font-medium text-brand-purple underline underline-offset-2 hover:text-brand-navy'
      >
        {label}
      </Link>
    )
  }

  if (!isApiLink) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-brand-purple underline underline-offset-2 hover:text-brand-navy"
      >
        {label}
      </a>
    )
  }

  return (
    <button
      type="button"
      disabled={state === 'downloading'}
      onClick={async () => {
        setState('downloading')
        try {
          await downloadAuthenticatedFile(href, `${label.replace(/\s+/g, '-').toLowerCase()}.pdf`)
          setState('idle')
        } catch {
          setState('error')
        }
      }}
      className={cn(
        'inline-flex items-center gap-1 font-medium underline underline-offset-2',
        state === 'error'
          ? 'text-destructive'
          : 'text-brand-purple hover:text-brand-navy',
        state === 'downloading' && 'opacity-60'
      )}
    >
      <Icons.download className="h-3.5 w-3.5" strokeWidth={1.5} />
      {state === 'downloading' ? 'Downloading…' : state === 'error' ? `${label} (retry)` : label}
    </button>
  )
}

interface ChatMessageProps {
  message: ChatMessageType
  userName?: string
  userImage?: string | null
  /** The AI Employee's name and photo key, for the face on her answers (plan 03 CHG-05). */
  assistantName?: string
  assistantPhoto?: string
  /** Re-asks the question of a broken stream (plan 03 D18 Retry). */
  onRetry?: (question: string) => void
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Render inline markdown (bold, code, italic) in text
 */
function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  // Match [link](url), **bold**, `code`, and *italic* — link must come
  // first in the alternation so "[label](url)" isn't first chewed up by
  // the other patterns matching inside its brackets.
  const regex = /(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)|(`[^`]+`)|(\*[^*]+\*)/g
  let lastIndex = 0
  let match
  let keyIndex = 0

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const segment = match[0]
    if (segment.startsWith('[')) {
      // Link: [label](url)
      const linkMatch = segment.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
      if (linkMatch) {
        // The model sometimes bolds/codes the label itself, e.g.
        // [**Download Report**](url) — strip that wrapping rather than
        // showing literal ** in the button text.
        const cleanLabel = linkMatch[1].replace(/^(\*\*|\*|`)(.*)\1$/, '$2')
        parts.push(<ChatLink key={keyIndex++} label={cleanLabel} href={linkMatch[2]} />)
      } else {
        parts.push(segment)
      }
    } else if (segment.startsWith('**') && segment.endsWith('**')) {
      // Bold
      parts.push(
        <strong key={keyIndex++} className="font-semibold text-foreground">
          {segment.slice(2, -2)}
        </strong>
      )
    } else if (segment.startsWith('`') && segment.endsWith('`')) {
      // Code
      parts.push(
        <code
          key={keyIndex++}
          className="px-1.5 py-0.5 bg-muted/50 rounded text-xs font-mono text-brand-navy"
        >
          {segment.slice(1, -1)}
        </code>
      )
    } else if (segment.startsWith('*') && segment.endsWith('*')) {
      // Italic
      parts.push(
        <em key={keyIndex++} className="italic">
          {segment.slice(1, -1)}
        </em>
      )
    }
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

/**
 * Render markdown content with support for headers, lists, blockquotes
 */
export function MarkdownContent({ content, className }: { content: string; className?: string }) {
  const lines = content.split('\n')
  
  return (
    <div className={cn('space-y-2 text-sm leading-relaxed', className)}>
      {lines.map((line, i) => {
        const trimmedLine = line.trim()
        
        // Empty line - add spacing
        if (!trimmedLine) {
          return <div key={i} className="h-1" />
        }
        
        // Headers (##, ###)
        if (trimmedLine.startsWith('### ')) {
          return (
            <h4 key={i} className="font-semibold text-foreground mt-3 first:mt-0">
              {renderInlineMarkdown(trimmedLine.slice(4))}
            </h4>
          )
        }
        if (trimmedLine.startsWith('## ')) {
          return (
            <h3 key={i} className="font-bold text-foreground mt-3 first:mt-0">
              {renderInlineMarkdown(trimmedLine.slice(3))}
            </h3>
          )
        }
        
        // Blockquote
        if (trimmedLine.startsWith('> ')) {
          return (
            <blockquote
              key={i}
              className="pl-3 border-l-2 border-brand-cornflower/50 text-muted-foreground italic"
            >
              {renderInlineMarkdown(trimmedLine.slice(2))}
            </blockquote>
          )
        }
        
        // Bullet points (* or -)
        if (/^[*\-]\s/.test(trimmedLine)) {
          return (
            <div key={i} className="flex gap-2 pl-1">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-cornflower/60 mt-2 flex-shrink-0" />
              <span>{renderInlineMarkdown(trimmedLine.replace(/^[*\-]\s/, ''))}</span>
            </div>
          )
        }
        
        // Numbered lists
        if (/^\d+\.\s/.test(trimmedLine)) {
          const match = trimmedLine.match(/^(\d+)\.\s(.*)/)
          if (match) {
            return (
              <div key={i} className="flex gap-2 pl-1">
                <span className="text-brand-purple font-medium min-w-[1.25rem]">
                  {match[1]}.
                </span>
                <span>{renderInlineMarkdown(match[2])}</span>
              </div>
            )
          }
        }
        
        // Regular paragraph
        return <p key={i}>{renderInlineMarkdown(trimmedLine)}</p>
      })}
    </div>
  )
}

/**
 * The "showing the work" lines of a streamed answer (plan 03 D18), from the PA
 * build's ThinkingSteps: the running step wears a pulsing purple dot, a finished
 * one a tick. The label is the key, so a step ticked off does not replay its
 * entrance. The list is announced politely once per change (Rule 14).
 */
export function ThinkingSteps({ steps }: { steps: StreamStep[] }) {
  return (
    <ol className='flex flex-col gap-2 py-1' aria-live='polite' aria-label='What the AI Employee is checking'>
      {steps.map((step) => {
        const done = step.state === 'done'
        return (
          <motion.li
            key={step.label}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className='flex items-start gap-2.5'
          >
            <span
              aria-hidden='true'
              className={cn(
                'mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border',
                done ? 'border-hairline bg-tab-track' : 'border-brand-cornflower bg-brand-cornflower/10'
              )}
            >
              {done ? (
                <Icons.check className='h-3.5 w-3.5 text-muted-foreground' strokeWidth={1.5} />
              ) : (
                <span className='h-2 w-2 rounded-full bg-brand-purple motion-safe:animate-pulse' />
              )}
            </span>
            <span className='flex flex-col gap-0.5'>
              {/* Brand purple, not cornflower, for the running label: cornflower text is
                  2.5:1 on white; purple is 5.9:1 (Rule 14). */}
              <span className={cn('text-base leading-snug', done ? 'text-muted-foreground' : 'font-semibold text-brand-purple')}>
                <span className='sr-only'>{done ? 'Done: ' : 'In progress: '}</span>
                {step.label}
              </span>
              {step.detail && <span className='text-sm leading-snug text-muted-foreground'>{step.detail}</span>}
            </span>
          </motion.li>
        )
      })}
    </ol>
  )
}

/** Three shimmering dots until the first fragment. Static under reduced motion. */
export function TypingDots({ label }: { label: string }) {
  return (
    <div className='flex items-center gap-2 py-1' role='status'>
      <div className='flex gap-1' aria-hidden='true'>
        <span className='h-2 w-5 rounded-full bg-gradient-to-r from-brand-muted/40 via-brand-muted/80 to-brand-muted/40 bg-[length:200%_100%] motion-safe:animate-shimmer' style={{ animationDelay: '0ms' }} />
        <span className='h-2 w-5 rounded-full bg-gradient-to-r from-brand-muted/40 via-brand-muted/80 to-brand-muted/40 bg-[length:200%_100%] motion-safe:animate-shimmer' style={{ animationDelay: '150ms' }} />
        <span className='h-2 w-5 rounded-full bg-gradient-to-r from-brand-muted/40 via-brand-muted/80 to-brand-muted/40 bg-[length:200%_100%] motion-safe:animate-shimmer' style={{ animationDelay: '300ms' }} />
      </div>
      <span className='text-sm text-muted-foreground'>{label}</span>
    </div>
  )
}

/** The 2px caret after the last character while text is arriving (aria-hidden). */
export function StreamCaret() {
  return (
    <span
      aria-hidden='true'
      className='ml-0.5 inline-block h-[1em] w-[2px] translate-y-[0.15em] bg-brand-purple motion-safe:animate-pulse'
    />
  )
}

export function ChatMessage({ message, userName, userImage, assistantName = 'Ava', assistantPhoto, onRetry }: ChatMessageProps) {
  const isUser = message.role === 'user'
  const steps = message.steps ?? []
  const hasSteps = steps.length > 0
  // Dots only while nothing at all has arrived; once steps show, they carry the wait.
  const waiting = Boolean(message.isLoading) && !hasSteps && !message.content
  const body = message.isStreaming ? trimUnfinishedBlocks(message.content) : message.content

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        {isUser ? (
          <Avatar
            src={userImage}
            fallback={userName || 'U'}
            size="sm"
          />
        ) : (
          <CoworkerAvatar name={assistantName} photo={assistantPhoto} size={32} alt='' />
        )}
      </div>

      {/* Message Content */}
      <div className={cn(
        'flex max-w-[85%] flex-col gap-1',
        isUser ? 'items-end' : 'items-start'
      )}>
        <div
          className={cn(
            'rounded-2xl px-4 py-3',
            'transition-all duration-200',
            isUser
              ? 'bg-brand-navy text-white rounded-br-md'
              : 'bg-white/90 text-foreground border border-border/50 rounded-bl-md shadow-soft'
          )}
          // The finished answer is read once, not fragment by fragment (Rule 14).
          aria-live={isUser ? undefined : 'polite'}
          aria-busy={isUser ? undefined : Boolean(message.isStreaming || message.isLoading)}
        >
          {waiting ? (
            <TypingDots label={`${assistantName} is thinking...`} />
          ) : isUser ? (
            <p className="text-sm whitespace-pre-wrap leading-relaxed">
              {message.content}
            </p>
          ) : (
            <>
              {hasSteps && <ThinkingSteps steps={steps} />}
              {body.trim() && (
                <div className={cn(hasSteps && 'mt-3 border-t border-hairline pt-3')}>
                  <MarkdownContent content={body} />
                  {message.isStreaming && <StreamCaret />}
                </div>
              )}
            </>
          )}
        </div>

        {message.streamBroken && (
          <div role='alert' className='flex items-center gap-2 pl-1 text-sm text-warning-strong'>
            <span>{assistantName} lost the thread.</span>
            {onRetry && message.retryOf && (
              <button
                type='button'
                onClick={() => onRetry(message.retryOf as string)}
                className='rounded font-semibold underline underline-offset-2 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
              >
                Retry
              </button>
            )}
          </div>
        )}

        {/* Timestamp */}
        <span className={cn(
          'text-xs text-muted-foreground',
          isUser ? 'pr-1' : 'pl-1'
        )}>
          {formatTime(message.timestamp)}
        </span>

        {/* Tool calls indicator */}
        {message.toolCalls && message.toolCalls.length > 0 && !message.isStreaming && (
          <div className="flex items-center gap-1.5 text-xs text-brand-purple pl-1">
            <Icons.zap className="h-3 w-3" strokeWidth={1.5} aria-hidden='true' />
            <span>Used {message.toolCalls.length} tool{message.toolCalls.length > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
    </div>
  )
}
